import { useCallback, useEffect, useRef } from 'react';
import { useUpdateCookingSession } from './mutations';

/** Long enough that typing a custom multiple saves once, not once per digit. */
const SAVE_DELAY_MS = 400;

/**
 * Saves the batch size the cook picks in the recipe viewer onto the cooking
 * session, so the viewer reopens at it (YC-35).
 *
 * A typed multiple or desired yield changes the size on every digit, so the
 * latest value is held back briefly and sent once. Closing the viewer sends
 * whatever is still held, so a size chosen just before closing is not lost.
 */
export function useSessionBatchScale(sessionId: number, delayMs: number = SAVE_DELAY_MS) {
  const { mutate } = useUpdateCookingSession();
  const pending = useRef<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (pending.current === null) return;
    const batchScale = pending.current;
    pending.current = null;
    mutate({ id: sessionId, data: { batchScale } });
  }, [mutate, sessionId]);

  // Runs on unmount, which is how closing the viewer ends it.
  useEffect(() => flush, [flush]);

  return useCallback(
    (batchScale: number) => {
      pending.current = batchScale;
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(flush, delayMs);
    },
    [flush, delayMs],
  );
}
